/* eslint-disable jsx-a11y/media-has-caption */
import { useState, useRef } from 'react'
import PropTypes from 'prop-types'
import { useTheme } from '@emotion/react'
import { graphql } from 'gatsby'
import {
  Button,
  Container,
  Section,
  SectionTitle,
} from 'chaoskit/src/components'
import { InstantSearch, Configure } from 'react-instantsearch-dom'
import { gradient } from 'chaoskit/src/assets/styles/utility'
import useUpdateEffect from 'react-use/lib/useUpdateEffect'
import { navigate } from '@reach/router'

import { getAlgoliaIndexName } from '../../utils/helpers'

import BaseLayout from '~layouts/BaseLayout'
import Data from '~components/Data'
import useAlgoliaSearchClient from '~hooks/useAlgoliaSearchClient'
import Hero, {
  HeroContent,
  HeroTitleWrapper,
  HeroTitleSub,
  HeroTitle,
} from '~partials/Hero'
import HelmetSEO from '~partials/global/HelmetSEO'
import {
  createUrl,
  urlToSearchState,
  searchStateToUrl,
} from '~partials/search/utility'
import { Hit, CustomCategoryMenu } from '~partials/search/algolia-case-studies'
import {
  CustomInfiniteHits,
  CustomClearRefinements,
  DEBOUNCE_TIME,
} from '~partials/search/algolia-global'
import CustomerLogos from '~partials/global/CustomerLogos'
import { VideoModal } from '~components/Video'
import track from '~helpers/track'

const { algoliaIndex } = getAlgoliaIndexName()

const Customers = ({
  data: {
    pageData: {
      childMdx: { frontmatter: pageData },
    },
  },
  location,
}) => {
  const showCategories = false
  const theme = useTheme()

  const videoRef = useRef()

  const searchClient = useAlgoliaSearchClient((state) => state.searchClient)
  const [searchState, setSearchState] = useState(urlToSearchState(location))
  const [debouncedSetState, setDebouncedSetState] = useState(null)
  const [customerVideoModalOpen, setCustomerVideoModalOpen] = useState(false)

  const handleSearchStateChange = (updatedSearchState) => {
    clearTimeout(debouncedSetState)

    setDebouncedSetState(
      setTimeout(() => {
        navigate(searchStateToUrl(updatedSearchState), updatedSearchState)
      }, DEBOUNCE_TIME)
    )

    setSearchState(updatedSearchState)
  }

  useUpdateEffect(() => {
    if (customerVideoModalOpen) {
      videoRef.current.pause()
    } else {
      videoRef.current.play()
    }
  }, [customerVideoModalOpen])

  return (
    <BaseLayout>
      <HelmetSEO title={pageData.title} description={pageData.description} />
      <InstantSearch
        indexName={algoliaIndex}
        searchClient={searchClient}
        searchState={searchState}
        onSearchStateChange={handleSearchStateChange}
        createURL={createUrl}
      >
        <Configure filters="articleLabel:'Case Studies'" hitsPerPage="6" />
        <Hero
          css={{
            ...gradient.dark(theme),

            '&::before': {
              content: "''",
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              opacity: theme.opacity.base,
              zIndex: 1,
              background: theme.color.dark.base,
            },
          }}
        >
          <video
            ref={videoRef}
            autoPlay
            loop
            muted
            playsInline
            css={{
              position: 'absolute',
              top: 0,
              left: 0,
              width: '100%',
              height: '100%',
              objectFit: 'cover',
              zIndex: 0,
            }}
          >
            <source src={pageData.hero.backgroundVideo} />
          </video>
          <HeroContent>
            <Container>
              <HeroTitleWrapper>
                <HeroTitle size="large">
                  <Data>{pageData.hero.heading}</Data>
                </HeroTitle>
                <HeroTitleSub>
                  <Data>{pageData.hero.blurb}</Data>
                </HeroTitleSub>
                <div css={{ marginTop: theme.space.large }}>
                  <Button
                    type="primary"
                    noContrast
                    onClick={() => {
                      track('Video watched', {
                        label: 'Featured customer',
                      })

                      setCustomerVideoModalOpen(true)
                    }}
                  >
                    Watch now
                  </Button>
                  <VideoModal
                    open={customerVideoModalOpen}
                    setIsOpen={setCustomerVideoModalOpen}
                    title="Customer video"
                    src={pageData.hero.video}
                  />
                </div>
              </HeroTitleWrapper>
            </Container>
          </HeroContent>
        </Hero>
        <Section
          css={{
            background: theme.brand.darkViolet,
            position: 'relative',
          }}
          space="base"
        >
          <Container className="u-contrast">
            <SectionTitle
              as="h3"
              title={<Data>{pageData.teams.heading}</Data>}
            />
          </Container>
          <CustomerLogos contrast />
        </Section>

        <Section>
          <Container>
            {showCategories && (
              <div
                css={{
                  [theme.mq.medium]: {
                    display: 'grid',
                    gridAutoColumns: '1fr',
                    gridAutoFlow: 'column',
                    alignItems: 'center',
                    gap: theme.space.base,
                  },
                }}
              >
                <div
                  css={{
                    [theme.mq.small__max]: {
                      display: 'none',
                    },

                    [theme.mq.medium]: {
                      gridColumn: 'span 2',
                      justifySelf: 'end',
                    },
                  }}
                >
                  <CustomCategoryMenu attribute="topics" limit={4} />
                </div>
              </div>
            )}
            {showCategories && <CustomClearRefinements />}
            <CustomInfiniteHits
              hitTemplate={Hit}
              customCss={{
                [theme.mq.medium]: {
                  gridTemplateColumns: 'repeat(2, 1fr)',
                },
              }}
            />
          </Container>
        </Section>
      </InstantSearch>
    </BaseLayout>
  )
}

Customers.propTypes = {
  data: PropTypes.object.isRequired,
  location: PropTypes.object.isRequired,
}

export const pageQuery = graphql`
  query CustomersPageData {
    pageData: file(
      name: { eq: "customers" }
      absolutePath: { regex: "/src/data/" }
    ) {
      childMdx {
        frontmatter {
          title
          description
          hero {
            blurb
            heading
            video
            backgroundVideo
          }
          teams {
            heading
          }
          community {
            heading
            tweets {
              status
            }
          }
        }
      }
    }
  }
`

export default Customers
